const TTL = 60 * 60 * 24 * 1000; // 1 day in ms.
const INVALIDATION_INTERVAL = 60 * 60 * 1000; // 1 hour in ms.
const LAST_INVALIDATED_KEY = "njorda_storage_last_invalidated";

// Cache management defaults
const DEFAULT_MAX_CACHE_SIZE_KB = 3000; // 3MB max for cache (leave ~2MB for other app data)

export const STORAGE_UPDATE_EVENT = "njorda-storage-update";

interface StoredItem<T> {
    value: T;
    validUntil: number;
}

export const setStorageItem = <T>(
    key: string,
    value: T,
    warnText = "Error setting storage item:",
    ttl = TTL,
    isRetry = false,
    managedPrefix?: string,
) => {
    if (!isLocalStorageAvailable()) {
        console.warn(warnText, "Local storage is not available");
        return;
    }

    const storeItem: StoredItem<T> = {
        value,
        validUntil: Date.now() + ttl,
    };
    try {
        localStorage.setItem(key, JSON.stringify(storeItem));
    } catch (error) {
        if (isRetry) {
            console.warn(warnText, error);
            return;
        }
        // On quota error, clean up and retry
        if (managedPrefix) {
            const currentSize = getStorageSize(managedPrefix);
             
            console.log(`Storage quota exceeded (${currentSize.toFixed(2)} KB), cleaning cache...`);
            evictOldestEntries(managedPrefix, DEFAULT_MAX_CACHE_SIZE_KB);
        } else {
            invalidateStorage(true); // Fallback to TTL-based invalidation
        }
        setStorageItem(key, value, warnText, ttl, true, managedPrefix);
    }
};
export const getStorageItem = <T>(key: string, warnText = "Error getting storage item:") => {
    if (!isLocalStorageAvailable()) {
        return null;
    }
    const rawValue = localStorage.getItem(key);
    if (!rawValue) {
        return null;
    }
    try {
        const storedItem = JSON.parse(rawValue);
        if (isStoredItemOfValidType(storedItem)) {
            if (Date.now() > storedItem.validUntil) {
                removeStorageItem(key);
                return null;
            }
            return storedItem.value as T;
        }
        // Fallback for old JSON format that wasn't wrapped in StoredItem
        return storedItem as T;
    } catch (error) {
        // Fallback for non-JSON data (like raw strings)
        console.warn(warnText, error);
        return rawValue as unknown as T;
    }
};

export const removeStorageItem = (key: string, warnText = "Error removing storage item:") => {
    if (!isLocalStorageAvailable()) {
        return;
    }
    try {
        localStorage.removeItem(key);
    } catch (error) {
        console.warn(warnText, error);
    }
};
export const clearStorage = (warnText = "Error clearing storage:") => {
    if (!isLocalStorageAvailable()) {
        return;
    }
    try {
        localStorage.clear();
    } catch (error) {
        console.warn(warnText, error);
    }
};
export const getStorageLength = (warnText = "Error getting storage length:") => {
    if (!isLocalStorageAvailable()) {
        return 0;
    }
    try {
        return localStorage.length;
    } catch (error) {
        console.warn(warnText, error);
        return 0;
    }
};
export const getStorageKey = (index: number, warnText = "Error getting storage key:") => {
    if (!isLocalStorageAvailable()) {
        return null;
    }
    try {
        return localStorage.key(index);
    } catch (error) {
        console.warn(warnText, error);
        return null;
    }
};

export const invalidateStorage = (force = false) => {
    if (!isLocalStorageAvailable()) {
        return;
    }

    const lastInvalidated = Number(localStorage.getItem(LAST_INVALIDATED_KEY) || 0);
    if (!force && Date.now() - lastInvalidated < INVALIDATION_INTERVAL) {
        return;
    }

    // Collect keys first to avoid index shifting when removing items
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key !== LAST_INVALIDATED_KEY) {
            keys.push(key);
        }
    }

    keys.forEach((key) => {
        const val = localStorage.getItem(key);
        if (val) {
            try {
                const storedItem = JSON.parse(val) as StoredItem<unknown>;
                if (isStoredItemOfValidType(storedItem)) {
                    if (Date.now() > storedItem.validUntil) {
                        localStorage.removeItem(key);
                    }
                }
            } catch (_e) {
                // Not our format, leave it alone unless we want to be aggressive
            }
        }
    });
    localStorage.setItem(LAST_INVALIDATED_KEY, Date.now().toString());
}

const isLocalStorageAvailable = () => {
    try {
        return typeof globalThis !== "undefined" && !!globalThis.localStorage;
    } catch (_e) {
        return false;
    }
}

const isStoredItemOfValidType = (storedItem: StoredItem<unknown>) => {
    return storedItem && typeof storedItem === "object" && "validUntil" in storedItem;
}

export const getStorageSize = (prefix?: string): number => {
    if (!isLocalStorageAvailable()) {
        return 0;
    }

    let totalSize = 0;

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (!prefix || key.startsWith(prefix))) {
            const value = localStorage.getItem(key);
            if (value) {
                totalSize += new Blob([value]).size;
            }
        }
    }

    return totalSize / 1024;
}

export const evictOldestEntries = (prefix: string, targetSizeKB: number): number => {
    if (!isLocalStorageAvailable()) {
        return 0;
    }

    const entries: Array<{ key: string; size: number }> = [];

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
            const value = localStorage.getItem(key);
            if (value) {
                entries.push({
                    key,
                    size: new Blob([value]).size / 1024,
                });
            }
        }
    }

    let currentSize = entries.reduce((sum, entry) => sum + entry.size, 0);
    let removedCount = 0;

    for (const entry of entries) {
        if (currentSize <= targetSizeKB) break;
        localStorage.removeItem(entry.key);
        currentSize -= entry.size;
        removedCount++;
    }

    return removedCount;
}

export const clearEntriesByPrefix = (prefix: string): number => {
    if (!isLocalStorageAvailable()) {
        return 0;
    }

    const keysToRemove: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith(prefix)) {
            keysToRemove.push(key);
        }
    }

    keysToRemove.forEach((key) => {
        localStorage.removeItem(key);
    });

    return keysToRemove.length;
}